/**
 * S7 — the posting screen. Where a count's fate is read, and never decided.
 *
 * A manager reaches it three ways, and it has to be honest on all three: by tapping
 * confirm on S6; by opening a count that is already `Posting` or `Posted` (S2 routes
 * both here, because this is the screen that renders an *outcome*); and by following
 * a bookmark from last Friday.
 *
 * **It polls, and it sends nothing.** `GET …/posting` at the cadence the *server*
 * names (`next_poll_after_seconds`, 3s backing off to 15s) — there is no interval,
 * no backoff and no timeout encoded in this bundle, because a second cadence policy
 * is a second thing to get wrong and the write-api harness, the backend and this
 * screen should pace Business Central identically. Polling stops at `terminal`:
 * there is nothing further to learn.
 *
 * **The one thing this screen must never do is offer to post again.** Not while a
 * post is in flight, not when a post is stranded, and least of all when a post
 * succeeded and a late step complained about it. The only forward action it carries
 * is *submit for review again*, and only for a count Business Central has said
 * outright did not post.
 *
 * The four outcomes live in `posting/PostingOutcome.tsx`, shared with the verifying
 * screen so the same state cannot be described two ways.
 */
import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { usePostingStatus } from "../api/queries";
import { PostingOutcome } from "../posting/PostingOutcome";
import { SubmitForReview } from "../review/SubmitForReview";
import { SessionFacts } from "./SessionFacts";
import type { SessionScreenProps } from "./SessionRoute";

export function Posting({ session, mine, areaId }: SessionScreenProps) {
  const poll = usePostingStatus(areaId, session.session_id, true);
  const status = poll.data;
  const queryClient = useQueryClient();
  const settled = useRef(false);

  // When the poll reaches a terminal outcome, re-read the session once.
  //
  // Two things depend on it and neither is cosmetic: the routing rule reads
  // `session.status`, and `SessionRoute` clears `post_attempted_at` on a terminal
  // session — the marker that would otherwise send the next visit to verifying for a
  // count that has already finished. Guarded by a ref because an invalidation that
  // re-triggered itself would poll BC forever.
  useEffect(() => {
    if (!status?.terminal || settled.current) return;
    settled.current = true;
    void queryClient.invalidateQueries({
      queryKey: ["count", "areas", areaId, "sessions", session.session_id],
    });
  }, [areaId, queryClient, session.session_id, status?.terminal]);

  const inFlight = session.status === "Posting";

  return (
    <section data-testid="posting-screen">
      <h2>{session.area.display_name} — posting</h2>

      {inFlight && (
        /* Standing, and above the poll: the sentence that matters most must not
           depend on a request having succeeded. This screen is reachable with no
           connection at all, and "do not post it again" is exactly as true then. */
        <div className="card notice" data-testid="posting-notice">
          <p>
            <strong>This count is posting in Business Central right now.</strong>{" "}
            Posting runs in a background session there, so the outcome arrives by
            polling — which this screen is doing.
          </p>
          <p>
            <strong>Do not post it again.</strong> Until Business Central reports an
            outcome, a second post would move the inventory twice.
          </p>
        </div>
      )}

      {poll.isLoading && <p className="muted">Asking Business Central…</p>}

      {poll.error && (
        <div className="card error" data-testid="poll-error">
          <p>Could not reach Business Central: {(poll.error as Error).message}</p>
          <p className="muted small">
            {/* The count's fate does not depend on this screen's connection, and
                saying so is the difference between a manager waiting and a manager
                posting again. */}
            This changes nothing about the count — its fate is whatever Business
            Central already holds. This screen keeps asking, and nothing is re-sent.
          </p>
        </div>
      )}

      {status && <PostingOutcome status={status} sessionId={session.session_id} />}

      {/* The one way forward, and only from the one state that has one: Business
          Central said outright that nothing posted. Owner-gated, like every action
          in this app, and it warns that resubmitting destroys the message above. */}
      {status?.failed && <SubmitForReview session={session} areaId={areaId} mine={mine} />}

      <div className="card">
        <h3>Session</h3>
        <SessionFacts session={session} mine={mine} />
      </div>

      <p>
        <Link to={`/areas/${encodeURIComponent(areaId)}`}>← Back to this area's counts</Link>
      </p>
    </section>
  );
}
